import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { patientService } from '../services/patientService'
import { DuplicateSuggestion } from '../types/patient'
import { formatPatientName } from '../utils/name'
import { formatDate } from '../utils/locale'
import './PatientDuplicateSuggestions.css'

interface PatientDuplicateSuggestionsProps {
  patientId: string
  duplicates: DuplicateSuggestion[]
  onLink?: () => void
}

export function PatientDuplicateSuggestions({
  patientId,
  duplicates,
  onLink,
}: PatientDuplicateSuggestionsProps) {
  const queryClient = useQueryClient()
  const [linking, setLinking] = useState<string | null>(null)
  const [ignoring, setIgnoring] = useState<string | null>(null)

  const linkMutation = useMutation({
    mutationFn: async (secondaryId: string) => {
      await patientService.linkPatients(patientId, secondaryId, false)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['patient', patientId] })
      queryClient.invalidateQueries({ queryKey: ['patients', 'duplicates', patientId] })
      queryClient.invalidateQueries({ queryKey: ['patients'] }) // Also invalidate patient list
      setLinking(null)
      onLink?.()
    },
    onError: (error: any) => {
      console.error('Error linking patients:', error)
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to link patients. Please try again.'
      alert(`Failed to link patients: ${errorMessage}`)
      setLinking(null)
    },
  })

  const ignoreMutation = useMutation({
    mutationFn: async (ignoredId: string) => {
      await patientService.ignoreDuplicate(patientId, ignoredId)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['patient', patientId] })
      queryClient.invalidateQueries({ queryKey: ['patients', 'duplicates', patientId] })
      setIgnoring(null)
    },
    onError: (error: any) => {
      console.error('Error ignoring duplicate suggestion:', error)
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to ignore duplicate. Please try again.'
      alert(`Failed to ignore duplicate: ${errorMessage}`)
      setIgnoring(null)
    },
  })

  const handleLink = async (duplicateId: string) => {
    if (window.confirm('Are you sure you want to link these patient records? This action can be reversed.')) {
      setLinking(duplicateId)
      linkMutation.mutate(duplicateId)
    }
  }

  const handleIgnore = async (duplicateId: string) => {
    if (window.confirm('Ignore this potential duplicate? It will no longer be suggested.')) {
      setIgnoring(duplicateId)
      ignoreMutation.mutate(duplicateId)
    }
  }

  // Only show the panel if there are duplicate matches
  if (duplicates.length === 0) {
    return null
  }

  return (
    <div className="duplicate-suggestions">
      <h3>Potential Duplicate Records</h3>
      <p className="duplicate-suggestions-description">
        The following records may be duplicates. Review the match reasons and link them if they represent the same patient.
      </p>
      <div className="duplicate-list">
        {duplicates.map((suggestion) => {
          const patient = suggestion.patient
          const isLinking = linking === patient.id
          const isIgnoring = ignoring === patient.id
          const confidenceClass = `confidence-${suggestion.confidence}`

          return (
            <div key={patient.id} className={`duplicate-item ${confidenceClass}`}>
              <div className="duplicate-item-header">
                <div className="duplicate-item-info">
                  <h4>{formatPatientName(patient)}</h4>
                  <div className="duplicate-item-meta">
                    {patient.patientNumber && <span>Patient #: {patient.patientNumber}</span>}
                    <span>DOB: {formatDate(patient.dateOfBirth)}</span>
                    {patient.ppsNumber && <span>PPS: {patient.ppsNumber}</span>}
                    {patient.gmsNumber && <span>GMS: {patient.gmsNumber}</span>}
                    {patient.phone && <span>Phone: {patient.phone}</span>}
                    {patient.email && <span>Email: {patient.email}</span>}
                  </div>
                </div>
                <div className="duplicate-item-score">
                  <div className={`score-badge ${confidenceClass}`}>
                    {suggestion.matchScore}% match
                  </div>
                  <div className={`confidence-badge ${confidenceClass}`}>
                    {suggestion.confidence}
                  </div>
                </div>
              </div>
              <div className="duplicate-item-reasons">
                <strong>Match reasons:</strong>
                <ul>
                  {suggestion.matchReasons.map((reason, idx) => (
                    <li key={idx}>{reason}</li>
                  ))}
                </ul>
              </div>
              <div className="duplicate-item-actions">
                <button
                  className="btn btn-primary"
                  onClick={() => handleLink(patient.id)}
                  disabled={isLinking || isIgnoring}
                >
                  {isLinking ? 'Linking...' : 'Link Records'}
                </button>
                <button
                  className="btn btn-secondary"
                  onClick={() => handleIgnore(patient.id)}
                  disabled={isLinking || isIgnoring}
                >
                  {isIgnoring ? 'Ignoring...' : 'Ignore'}
                </button>
                <button
                  className="btn btn-secondary"
                  onClick={() => window.open(`/patients/${patient.id}`, '_blank')}
                >
                  View Record
                </button>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
